import { Building2Icon, CheckIcon, LoaderIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSearchTickers } from "@/hooks/useTickerQueries";
import { type ClassValue, cn } from "@/lib/utils";

interface TickerAutoCompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (symbol: string, name: string) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: ClassValue;
  inputClassName?: ClassValue;
}

interface TickerSuggestion {
  type: "ticker";
  symbol: string;
  name: string;
  exchange: string;
}

interface SectionHeader {
  type: "header";
  title: string;
}

type SuggestionItem = TickerSuggestion | SectionHeader;

export const TickerAutoComplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "Enter ticker symbol or company name",
  disabled = false,
  autoFocus = false,
  className,
  inputClassName,
}: TickerAutoCompleteProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Enable search when user types at least 2 characters
  const { data, isLoading } = useSearchTickers(value, {
    enabled: value.length >= 2 && isOpen && !disabled,
  });

  // Combine user tickers and master tickers with section headers
  const suggestions = getSectionedSuggestions(data);

  // Get selectable items (filter out headers)
  const selectableItems = suggestions.filter((item) => item.type !== "header");

  // Get selectable index from overall index
  const getSelectableIndex = (overallIndex: number): number => {
    let offset = 0;
    for (let i = 0; i < overallIndex; i++) {
      if (suggestions[i].type === "header") {
        offset++;
      }
    }
    return overallIndex - offset;
  };

  // Get overall index from selectable index
  const getOverallIndex = (selectableIndex: number): number => {
    let overallIndex = -1;
    let found = 0;
    for (let i = 0; i < suggestions.length; i++) {
      const item = suggestions[i];
      if (item.type !== "header") {
        if (found === selectableIndex) {
          overallIndex = i;
          break;
        }
        found++;
      }
    }
    return overallIndex;
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.toUpperCase();
    onChange(newValue);
    setIsOpen(true);
    setSelectedIndex(0);
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion: TickerSuggestion) => {
    onChange(suggestion.symbol);
    onSelect(suggestion.symbol, suggestion.name);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || selectableItems.length === 0) {
      if (e.key === "Enter" && value.trim()) {
        // User pressed Enter with custom value
        onSelect(value.trim(), value.trim());
        setIsOpen(false);
      }
      return;
    }

    const currentSelectableIndex = getSelectableIndex(selectedIndex);

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const nextSelectable = currentSelectableIndex < selectableItems.length - 1 ? currentSelectableIndex + 1 : currentSelectableIndex;
        setSelectedIndex(getOverallIndex(nextSelectable));
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prevSelectable = currentSelectableIndex > 0 ? currentSelectableIndex - 1 : 0;
        setSelectedIndex(getOverallIndex(prevSelectable));
        break;
      }
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          const item = suggestions[selectedIndex];
          if (item.type !== "header") {
            handleSelectSuggestion(item as TickerSuggestion);
          }
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const listItems = listRef.current.querySelectorAll("li");
      const selectedItem = listItems[selectedIndex] as HTMLElement;
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        listRef.current &&
        !listRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          className={cn(
            "w-full px-3 py-2 pr-10 border rounded-md bg-background",
            "focus:outline-none focus:ring-2 focus:ring-primary",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            inputClassName,
          )}
        />
        {isLoading && value.length >= 2 && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <LoaderIcon className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && value.length >= 2 && isOpen && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Building2Icon className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
      </div>

      {isOpen && value.length >= 2 && (
        <ul
          ref={listRef}
          className={cn("absolute z-50 w-full mt-1 max-h-60 overflow-auto rounded-md border bg-popover shadow-md", "focus:outline-none")}
        >
          {isLoading ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">Searching...</li>
          ) : suggestions.length > 0 ? (
            suggestions.map((item, index) => {
              if (item.type === "header") {
                return (
                  <li
                    key={`header-${item.title}`}
                    className="px-3 py-1 text-xs font-semibold text-muted-foreground bg-muted/30 select-none border-b border-border"
                  >
                    {item.title}
                  </li>
                );
              }
              const suggestion = item as TickerSuggestion;
              return (
                <li
                  key={suggestion.symbol}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  className={cn(
                    "px-3 py-2 cursor-pointer text-sm flex items-center justify-between",
                    "hover:bg-accent hover:text-accent-foreground",
                    index === selectedIndex && "bg-accent",
                  )}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{suggestion.symbol}</span>
                    <span className="text-xs text-muted-foreground truncate max-w-[250px]">{suggestion.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{suggestion.exchange}</span>
                    {index === selectedIndex && <CheckIcon className="w-4 h-4 text-primary" />}
                  </div>
                </li>
              );
            })
          ) : value.length >= 2 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">No results found. Press Enter to create "{value}"</li>
          ) : (
            <li className="px-3 py-2 text-sm text-muted-foreground">Type at least 2 characters to search</li>
          )}
        </ul>
      )}
    </div>
  );
};

/**
 * Generates sectioned suggestions combining user tickers and master tickers
 */
function getSectionedSuggestions(searchResults?: {
  userTickers?: { symbol: string; displayName: string; exchange: string }[];
  masterTickers?: { symbol: string; name: string; exchange: string }[];
}): SuggestionItem[] {
  const suggestions: SuggestionItem[] = [];

  const userTickers = searchResults?.userTickers ?? [];
  const masterTickers = searchResults?.masterTickers ?? [];

  // Add My Tickers section if there are user tickers
  if (userTickers.length > 0) {
    suggestions.push({ type: "header", title: "我的股票" });
    userTickers.forEach((ticker) => {
      suggestions.push({
        type: "ticker",
        symbol: ticker.symbol,
        name: ticker.displayName,
        exchange: ticker.exchange,
      });
    });
  }

  // Add All Stocks section if there are master tickers
  if (masterTickers.length > 0) {
    suggestions.push({ type: "header", title: "全部股票" });
    masterTickers.forEach((ticker) => {
      suggestions.push({
        type: "ticker",
        symbol: ticker.symbol,
        name: ticker.name,
        exchange: ticker.exchange,
      });
    });
  }

  return suggestions;
}
